// TrainingPaces.tsx

import React from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  calculatePredictedTime,
  convertSecondsToTime,
} from "./RaceTimePredictor";
import { calculatePace, DistanceUnit, PaceUnit } from "./PaceCalculator";

interface TrainingPacesProps {
  distanceUnit: DistanceUnit;
  distance: string;
  paceUnit: PaceUnit;
  hours: string;
  minutes: string;
  seconds: string;
}

const trainingPacesConfig = {
  "VO2 Max": {
    percentage: 1,
    referenceDistance: 5,
    referenceDistanceUnit: "km",
  },
  "Lactate Threshold": {
    percentage: 1,
    referenceDistance: 13.1,
    referenceDistanceUnit: "miles",
  },
  "General Aerobic": {
    percentage: 1.15,
    referenceDistance: 26.2,
    referenceDistanceUnit: "miles",
  },
  "Long Run": {
    percentage: 1.1,
    referenceDistance: 26.2,
    referenceDistanceUnit: "miles",
  },
  Recovery: {
    percentage: 1.3,
    referenceDistance: 26.2,
    referenceDistanceUnit: "miles",
  },
};

const calculatePaceInSeconds = (
  hours: string,
  minutes: string,
  seconds: string
) => {
  return Number(hours) * 60 * 60 + Number(minutes) * 60 + Number(seconds);
};

const TrainingPaces: React.FC<TrainingPacesProps> = ({
  distanceUnit,
  paceUnit,
  distance,
  hours,
  minutes,
  seconds,
}) => {
  const raceTimeInSeconds = calculatePaceInSeconds(hours, minutes, seconds);

  distance = Number(distance);

  const trainingPaces = Object.entries(trainingPacesConfig).map(
    ([key, config]) => {
      const { referenceDistance, percentage, referenceDistanceUnit } = config;

      // TODO: Make sure both distances are same units
      const normalisedDistance =
        distanceUnit === referenceDistanceUnit
          ? distance
          : referenceDistanceUnit === "km"
          ? distanceUnit === "meters"
            ? distance / 1000
            : distance * 1.60934
          : distance * 0.621371;

      const referencePredictedTime = calculatePredictedTime(
        normalisedDistance as number,
        raceTimeInSeconds,
        referenceDistance,
        "riegel", // Use the desired algorithm
        true
      ) as number;
      const adjustment = percentage;
      const adjustedPredictedTimeSeconds = referencePredictedTime * adjustment;

      const adjustedPredictedTime = convertSecondsToTime(
        adjustedPredictedTimeSeconds
      );
      const { hours, minutes, seconds } = adjustedPredictedTime;
      const adjustedPredictedPace = calculatePace(
        referenceDistance,
        hours,
        minutes,
        seconds,
        referenceDistanceUnit,
        paceUnit
      );

      return {
        trainingType: key,
        pace: adjustedPredictedPace,
      };
    }
  );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Session Type</TableCell>
            <TableCell align="right">Recommended Pace</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trainingPaces.map((training) => (
            <TableRow key={training.trainingType}>
              <TableCell>{training.trainingType}</TableCell>
              <TableCell align="right">
                {training.pace.paceMinutesCalc.toString().padStart(2, "0")}:
                {training.pace.paceSecondsCalc.toString().padStart(2, "0")}/
                {paceUnit}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrainingPaces;
